import styled from '@emotion/styled';
import { Button, Dropdown, Input, Menu, message, Select, Spin } from 'antd';
import { TextAreaRef } from 'antd/lib/input/TextArea';
import { useFormikContext } from 'formik';
import useSmartField from 'hooks/useSmartField';
import produce from 'immer';
import { HTMLAttributes, useRef, useState } from 'react';
import { useTranslate } from '@tolgee/react';
import insertAtCursor from 'utils/insertAtCursor';
import { itemList, KeywordType } from './api';
import { FV, getItemsCheckedNext, isItemSelected } from './share';

const Container = styled.div`
  height: 100%;
  border: solid 1px #f0f2f5;
  .ant-spin-nested-loading {
    height: 100%;
  }
  .ant-spin-container {
    display: flex;
    flex-direction: column;
    height: 100%;
    > header,
    > footer {
      display: flex;
      align-items: center;
      flex: 0 0 44px;
      height: 44px;
      padding: 0 12px;
    }
    > header {
      border-bottom: solid 1px #f0f2f5;
      > span {
        flex: 0 0 auto;
      }
      > div {
        flex: 1 0 0;
      }
    }
    > footer {
      border-top: solid 1px #f0f2f5;
    }
    > textarea {
      flex: 1 0 0;
      padding: 9px 11px;
      line-height: 28px;
      border: none;
      resize: none;
      box-shadow: none !important;
    }
  }
`;

export interface QuickSelectProps extends HTMLAttributes<HTMLDivElement> {}

function QuickSelect(props: QuickSelectProps) {
  const { ...otherProps } = props;

  const { t } = useTranslate();

  const {
    values: { platform, region, stores, skuEnable },
  } = useFormikContext<FV>();

  type Type = Exclude<KeywordType, 1>;

  const typeLabelMap = {
    2: t('sider.商品ID'),
    3: t('aide.主商品货号'),
    ...(skuEnable === true ? { 4: t('sider.SKU货号') } : {}),
  };

  const options = Object.entries(typeLabelMap).map(([value, label]) => {
    return { value: parseInt(value, 10), label };
  });

  const typePlaceholderMap = {
    2: t('aide.输入x可快速选中，多个商品以Enter分隔', { x: t('sider.商品ID') }),
    3: t('aide.输入x可快速选中，多个商品以Enter分隔', { x: t('aide.主商品货号') }),
    4: t('aide.输入x可快速选中，多个商品以Enter分隔', { x: t('sider.SKU货号') }),
  };

  const {
    field: { value: selectedItems, onChange: onSelectedItemsChange },
  } = useSmartField<FV['selectedItems']>('selectedItems');

  const {
    field: { value: selectFlag, onChange: onSelectFlagChange },
  } = useSmartField<FV['selectFlag']>('selectFlag');

  const [selectType, setSelectType] = useState<Type>(2);

  const [value, setValue] = useState('');

  const [loading, setLoading] = useState(false);

  const textareaRef = useRef<TextAreaRef>(null);

  const onPaste = () => {
    navigator.clipboard
      .readText()
      .then((text) => {
        const { current: textarea } = textareaRef;

        if (textarea && text) {
          const { resizableTextArea } = textarea;

          if (resizableTextArea) {
            const { textArea } = resizableTextArea;

            const nextValue = insertAtCursor(textArea, text);

            setValue(nextValue);
          }
        }
      })
      .catch(() => {});
  };

  const onQuickSelect = () => {
    setLoading(true);

    const ids = value
      .split('\n')
      .map((_) => _.trim())
      .filter((_) => _);

    const pageSize = 100;

    const loopFetch = (list: Aide.Item[] = [], pageNo = 1): Promise<Aide.Item[]> => {
      return itemList({
        externalType: platform,
        region,
        externalStoreId: stores.map(({ storeId }) => storeId),
        keywordType: selectType,
        keyword: ids.join(','),
        pageNo,
        pageSize,
      }).then(({ itemList }) => {
        const next = [...list, ...itemList];

        if (itemList.length < pageSize) {
          return next;
        }

        return loopFetch(next, pageNo + 1);
      });
    };

    loopFetch()
      .then((itemList) => {
        const unfoundIds = ids.filter((id) => {
          return itemList.every((item) => {
            const { externalItemId, itemSku, skuInfo } = item;

            if (selectType === 2) {
              return id !== externalItemId;
            }

            if (selectType === 3) {
              return id !== itemSku;
            }

            return skuInfo.every(({ sellerSku }) => sellerSku !== id);
          });
        });

        if (unfoundIds.length === 0) {
          message.success(t('aide.已选中x项', { x: itemList.length }));

          setValue('');
        } else {
          message.warn(t('aide.x项未找到对应商品', { x: unfoundIds.length }));

          setValue(unfoundIds.join('\n'));
        }

        if (itemList.length === 0) {
          return;
        }

        if (selectType === 2 || selectType === 3) {
          onSelectedItemsChange(getItemsCheckedNext(selectedItems, itemList, true));
        } else {
          const next = produce(selectedItems, (draft) => {
            ids.forEach((id) => {
              const targets = itemList.filter(({ skuInfo }) =>
                skuInfo.some(({ sellerSku }) => sellerSku === id),
              );

              targets.forEach((target) => {
                const targetSku = target.skuInfo.find(({ sellerSku }) => sellerSku === id);

                if (!targetSku) {
                  return;
                }

                const { skuId } = targetSku;

                const selectedTarget = draft.find((selectedItem) =>
                  isItemSelected(selectedItem, target),
                );

                if (!selectedTarget) {
                  draft.push({
                    externalStoreId: target.externalShopId,
                    spuId: target.externalItemId,
                    skuIds: [skuId],
                  });

                  return;
                }

                const { skuIds } = selectedTarget;

                if (skuIds.length === 0 || skuIds.includes(skuId)) {
                  return;
                }

                skuIds.push(skuId);
              });
            });
          });

          onSelectedItemsChange(next);
        }

        onSelectFlagChange(selectFlag + 1);
      })
      .catch(() => {
        message.error(t('phrase.获取数据失败，请稍后再试'));
      })
      .finally(() => {
        setLoading(false);
      });
  };

  return (
    <Container {...otherProps}>
      <Spin spinning={loading}>
        <header>
          <span>{t('aide.选择类型')}</span>
          <Select value={selectType} onChange={setSelectType} options={options} />
        </header>
        <Dropdown
          trigger={['contextMenu']}
          overlay={
            <Menu>
              <Menu.Item onClick={onPaste}>{t('ViewsChat.粘贴')}</Menu.Item>
            </Menu>
          }
        >
          <Input.TextArea
            ref={textareaRef}
            placeholder={typePlaceholderMap[selectType]}
            value={value}
            onChange={({ target: { value } }) => setValue(value)}
          />
        </Dropdown>
        <footer>
          <Button
            block
            ghost
            type="primary"
            disabled={!value || !value.trim()}
            onClick={onQuickSelect}
          >
            {t('aide.快速选中')}
          </Button>
        </footer>
      </Spin>
    </Container>
  );
}

export default QuickSelect;
